/**
 * section-role.tsx — Mục "Vai trò & giọng" của màn nhân viên AI (Cần phát hành): Nhân vật (menu tạo / tối ưu / kiểm tra,
 * "Chép từ mẫu" = Mẫu nhân vật của Thư mục dùng chung), giọng điệu, độ dài câu trả lời, ngôn ngữ, câu khi không có thông tin.
 */
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Copy, LayoutTemplate, ShieldCheck, Sparkles, UserRound, WandSparkles, History } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useAiLibrary } from '@/hooks/use-ai-khung'
import { useAuthStore } from '@/stores/auth-store'
import { LearnHistoryDialog } from '../learn-history-dialog'
import { ChipGroup } from '../train/train-ui'
import { InsertVariableButton } from '../train/insert-variable-button'
import { PersonaAiDialog, type PersonaAiTab } from '../train/persona-ai-dialog'
import { PersonaCheckDialog } from '../train/persona-check-dialog'
import { StaffPanel } from './staff-ui'
import {
  LENGTH_PRESET_OPTIONS, OUTPUT_LANGUAGES, TONE_PRESET_OPTIONS, insertAtCursor, personaCounter, type OutputLanguage,
} from '../train/bot-settings'
import type { SectionProps } from '../train/draft-form'

export function RoleSection(props: SectionProps) {
  const { t } = useTranslation('aiStaff')
  const { botId, form, set, setSettings, canEdit } = props
  // "Học từ hội thoại": CHỈ chủ sở hữu (đọc hội thoại cũ của cả tổ chức) — khớp learn-history-routes.ts.
  const role = useAuthStore((st) => st.user?.role)
  const canLearn = role === 'owner'
  const { data: library } = useAiLibrary()
  const personaTemplate = library?.templates.persona?.trim() || ''
  const s = form.settings
  const personaRef = useRef<HTMLTextAreaElement>(null)
  const [aiDialog, setAiDialog] = useState<{ tab: PersonaAiTab; request?: string } | null>(null)
  const [checkOpen, setCheckOpen] = useState(false)
  const [learnOpen, setLearnOpen] = useState(false)
  const counter = personaCounter(form.personaPrompt.length)

  const copyTemplate = () => {
    if (!personaTemplate) return
    if (form.personaPrompt.trim() && !window.confirm(t('role.confirmReplace'))) return
    set({ personaPrompt: personaTemplate })
    toast.success(t('role.copied'))
  }

  const insertToken = (token: string) => {
    const el = personaRef.current
    const { text, cursor } = insertAtCursor(form.personaPrompt, token, el?.selectionStart, el?.selectionEnd)
    set({ personaPrompt: text })
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(cursor, cursor) })
  }

  return (
    <StaffPanel
      id="staff-vai-tro" title={t('role.title')} icon={<UserRound className="h-4 w-4" />} scope="publish"
      description={t('role.description')}
    >
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[180px] flex-1">
            <p className="text-sm font-medium">{t('role.persona.title')}</p>
            <p className="text-xs text-muted-foreground">{t('role.persona.description')}</p>
          </div>
          {/* Không có quyền sửa (`ai.update`) → không hiện các nút soạn nhân vật. */}
          {canEdit && (
            <Button size="sm" variant="outline" disabled={!personaTemplate} onClick={copyTemplate}
              title={personaTemplate ? t('role.persona.copyTitle') : t('role.persona.copyTitleEmpty')}>
              <Copy className="h-3.5 w-3.5" /> {t('role.persona.copyFromTemplate')}
            </Button>
          )}
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <WandSparkles className="h-3.5 w-3.5 text-primary" /> {t('role.persona.createOptimize')} <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem disabled={!form.personaPrompt.trim()} onSelect={() => setCheckOpen(true)}>
                  <ShieldCheck className="h-4 w-4" /> {t('role.persona.check')}
                </DropdownMenuItem>
                {canLearn && (
                  <DropdownMenuItem onSelect={() => setLearnOpen(true)}>
                    <History className="h-4 w-4" /> {t('role.persona.learn')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setAiDialog({ tab: form.personaPrompt.trim() ? 'optimize' : 'create' })}>
                  <Sparkles className="h-4 w-4" /> {t('role.persona.aiCreate')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setAiDialog({ tab: 'template' })}>
                  <LayoutTemplate className="h-4 w-4" /> {t('role.persona.useTemplate')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canEdit && <InsertVariableButton botId={botId} onInsert={insertToken} attributes={s.collectAttributes} />}
        </div>
        <textarea
          ref={personaRef}
          className="min-h-[260px] w-full resize-y rounded-md border bg-transparent p-3 font-mono text-xs leading-relaxed outline-none focus:ring-1 focus:ring-ring"
          value={form.personaPrompt}
          onChange={(e) => set({ personaPrompt: e.target.value })}
          placeholder={t('role.persona.placeholder')}
          disabled={!canEdit}
        />
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-muted-foreground">{t('role.persona.emptyHint')}</span>
          <span className={counter.over ? 'font-medium text-destructive' : counter.near ? 'text-warning' : 'text-muted-foreground'}>
            {counter.text}{counter.over && t('role.persona.tooLong')}
          </span>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">{t('role.tone.label')}</Label>
          <ChipGroup
            value={s.tone.preset ?? 0}
            options={[{ value: 0, label: t('role.byPersona') }, ...TONE_PRESET_OPTIONS]}
            onChange={(v) => setSettings((x) => ({ ...x, tone: { ...x.tone, preset: v || null } }))}
            disabled={!canEdit}
          />
          <Input
            className="h-8 text-xs" maxLength={500} value={s.tone.custom}
            onChange={(e) => setSettings((x) => ({ ...x, tone: { ...x.tone, custom: e.target.value } }))}
            placeholder={t('role.tone.placeholder')} disabled={!canEdit}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t('role.length.label')}</Label>
          <ChipGroup
            value={s.answerLength.preset ?? 0}
            options={[{ value: 0, label: t('role.byPersona') }, ...LENGTH_PRESET_OPTIONS]}
            onChange={(v) => setSettings((x) => ({ ...x, answerLength: { ...x.answerLength, preset: v || null } }))}
            disabled={!canEdit}
          />
          <Input
            className="h-8 text-xs" maxLength={500} value={s.answerLength.custom}
            onChange={(e) => setSettings((x) => ({ ...x, answerLength: { ...x.answerLength, custom: e.target.value } }))}
            placeholder={t('role.length.placeholder')} disabled={!canEdit}
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">{t('role.language.label')}</Label>
        <Select value={s.outputLanguage} onValueChange={(v) => setSettings((x) => ({ ...x, outputLanguage: v as OutputLanguage }))} disabled={!canEdit}>
          <SelectTrigger className="sm:max-w-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {OUTPUT_LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">{t('role.unknown.label')} <span className="font-normal text-muted-foreground">{t('role.unknown.hint')}</span></Label>
        <Input
          maxLength={500} value={s.unknownReply}
          onChange={(e) => setSettings((x) => ({ ...x, unknownReply: e.target.value }))}
          placeholder={t('role.unknown.placeholder')}
          disabled={!canEdit}
        />
      </div>

      {aiDialog && (
        <PersonaAiDialog
          open
          onOpenChange={(o) => { if (!o) setAiDialog(null) }}
          botId={botId}
          current={form.personaPrompt}
          initialTab={aiDialog.tab}
          initialRequest={aiDialog.request}
          onApply={(text) => set({ personaPrompt: text })}
        />
      )}
      <PersonaCheckDialog
        open={checkOpen}
        onOpenChange={setCheckOpen}
        text={form.personaPrompt}
        onOptimize={(request) => setAiDialog({ tab: 'optimize', request })}
      />
      <LearnHistoryDialog
        open={learnOpen} onOpenChange={setLearnOpen} botId={botId}
        onApplied={(text) => set({ personaPrompt: text })}
      />
    </StaffPanel>
  )
}
